/**
 * The dock's streaming chunk handlers, and the tool-progress state they drive.
 *
 * Extracted from `Dock.tsx` by TASK-794 — the single largest block in a 637-line
 * component, and the one with the least to do with the rest of it. Everything
 * here answers one question: what does this surface do with each chunk type the
 * agent streams?
 *
 * Two rules the extraction preserves and makes visible:
 *
 * - **Side channels belong to the PLACEMENT, not the thread.** Which chunks this
 *   surface can render is a property of the screen, not of the conversation, so
 *   the meeting/kyc flags are passed in from the placement (TASK-769). A task
 *   thread opened on meeting prep must still handle `brief_update`.
 * - **The declared contract and the runtime behaviour must agree.** The returned
 *   `ignored` set comes from `SCOPE_CHUNK_CONTRACTS`, which `chunkContract.test`
 *   asserts is exhaustive per scope — an undeclared chunk type is a test
 *   failure, not a silent drop (TASK-710).
 */

import { useState } from 'react';
import { notifyToolFinished } from '../../stores/widgetRefreshStore';
import {
  makeArtifactCardInterceptor,
  type AgenticChunk,
  type ChunkHandlerMap,
  type HardGateConfirm,
} from '../../hooks/useAgenticChat';
import { SCOPE_CHUNK_CONTRACTS } from '../../types/chunkContracts';
import type { StreamingChunkType } from '../../types/chat.types';
import type { ArtifactCardProps } from '../canvas/widgets/ArtifactCard';
import type { Artifact, ChatAction, ChatSource } from '../../types/chat.types';
import type { ToolProgressEvent } from './ToolProgress';
import type { ChatStoreState } from '../../stores/createChatStore';
import type { DockSideChannels } from '../../stores/dockChatStores';
import { cleanKGValue } from '../../utils/kgFieldMapping';

/** The slice of the scope store these handlers write into. */
type DockStoreHandle = { getState: () => ChatStoreState<DockSideChannels> };

export interface UseDockChunkHandlersArgs {
  store: DockStoreHandle;
  isMeetingScope: boolean;
  /**
   * The placement resolves a client, so an Apply has somewhere to write. Was
   * `isKycScope` — see the note on the `extraction_proposal` handler below.
   */
  canApplyProposals: boolean;
  onConfirm: (confirm: HardGateConfirm) => void;
}

export interface UseDockChunkHandlersResult {
  handlers: ChunkHandlerMap;
  ignored: StreamingChunkType[];
  toolEvents: ToolProgressEvent[];
  /** Cleared by the owner when a turn ends. */
  setToolEvents: React.Dispatch<React.SetStateAction<ToolProgressEvent[]>>;
}

export function useDockChunkHandlers({
  store,
  isMeetingScope,
  canApplyProposals,
  onConfirm,
}: UseDockChunkHandlersArgs): UseDockChunkHandlersResult {
  const [toolEvents, setToolEvents] = useState<ToolProgressEvent[]>([]);

  const handlers: ChunkHandlerMap = {
    artifact: (chunk: AgenticChunk) => {
      if (chunk.artifact) store.getState().addPending('artifacts', chunk.artifact as Artifact);
    },
    action: (chunk: AgenticChunk) => {
      if (chunk.action) store.getState().addPending('actions', chunk.action as ChatAction);
    },
    source: (chunk: AgenticChunk) => {
      if (chunk.source) store.getState().addPending('sources', chunk.source as ChatSource);
    },
    status: () => {},
    // TASK-768: creation results stay IN this conversation as cards; every other
    // widget keeps flowing to the canvas sink (TASK-492 architecture). Without
    // this key the chunk fell through to `canvasWidgetHandler`, which put the
    // card in canvasStore and auto-opened the z-40 CanvasDrawer over the z-30
    // transcript the advisor was reading. The interceptor is shared with the
    // rail (TASK-742/790); only the destination differs — a pending slot, so the
    // card survives reload and scope change, which the rail does not need.
    widget: makeArtifactCardInterceptor<ArtifactCardProps>((props) =>
      store.getState().addPending('artifactCards', props)
    ),
    tool_start: (chunk: AgenticChunk) => {
      // TASK-792: the router has chosen and a tool is running — no longer
      // "routing". The advisor sees the difference (see the working strip).
      store.getState().setTurn('working');
      const tool = (chunk.tool_name as string) || 'tool';
      setToolEvents((prev) => [
        ...prev,
        {
          id: `${tool}-${prev.length}`,
          label: `Running ${tool}…`,
          status: 'running',
          startedAt: Date.now(),
        },
      ]);
    },
    tool_end: (chunk: AgenticChunk) => {
      const failed = chunk.status === 'failed';
      // A finished tool may have changed what a mounted widget is showing; the widget
      // declares which tools matter to it (see `useRefreshTick`).
      if (!failed && chunk.tool_name) notifyToolFinished(chunk.tool_name as string);
      setToolEvents((prev) => {
        const idx = prev.findIndex((e) => e.status === 'running');
        if (idx === -1) return prev;
        const next = [...prev];
        next[idx] = { ...next[idx], status: failed ? 'failed' : 'completed', endedAt: Date.now() };
        return next;
      });
    },
    hard_gate_confirm: (chunk: AgenticChunk) => {
      // TASK-792: waiting on a human decision is its own state, not a turn that
      // happens to still be "streaming".
      store.getState().setTurn('blocked');
      if (chunk.confirm) onConfirm(chunk.confirm);
    },
    // TASK-716 — field/DNA proposals (values cleaned like the old panel did).
    //
    // This was gated on `isKycScope`, which silently discarded every proposal raised on a
    // client page: `derive_dna` is granted to the client and personalization specialists too,
    // so a derivation asked for from /clients/:id streamed 14 proposals into a handler that
    // was never installed (observed live 2026-08-24 — "Generated 14 extraction proposals",
    // nothing on screen). What an Apply needs is not the KYC screen but a CLIENT to write
    // against: `applyKYCProposal` sends `values.*` straight to the graph and no-ops the form
    // when no KYC form is mounted. So the gate is the client, not the surface.
    ...(canApplyProposals
      ? {
          extraction_proposal: (chunk: AgenticChunk) => {
            const raw = chunk.extraction_proposals as
              | Array<{ field_path: string; value: unknown; confidence: number; source?: string }>
              | undefined;
            if (raw?.length) {
              for (const p of raw) {
                store
                  .getState()
                  .addPending('extractionProposals', {
                    ...p,
                    value: cleanKGValue(p.value),
                  } as never);
              }
            }
          },
        }
      : {}),
    // TASK-869: a task proposal is NOT a meeting side channel. `propose_action` is granted on
    // every surface that can claim a task (registry `_TASK_SPINE`), and its only user-visible
    // product is this chunk — so installing the handler for the meeting scope alone meant the
    // backend produced an Apply card and the dock dropped it on the floor. That is the defect
    // TASK-869 exists to remove, one layer out: the advisor is told a task was put up for them
    // and there is nothing on screen. `ActionProposalCard` needs no meeting context — it POSTs
    // `/api/tasks` with the proposal's own data — so the card is safe wherever the dock renders.
    action_proposal: (chunk: AgenticChunk) => {
      if (chunk.action_proposal)
        store.getState().addPending('actionProposals', chunk.action_proposal as never);
    },
    // TASK-715 — meeting scope side channels (no-ops declared-ignored elsewhere). These two
    // genuinely are meeting-shaped: `brief_update` writes the meeting brief, and only a
    // `blocked` proposal_action renders — the rest feed the PreparationPage composer bridge.
    ...(isMeetingScope
      ? {
          brief_update: (chunk: AgenticChunk) => {
            if (chunk.brief_update)
              store.getState().addPending('briefUpdates', chunk.brief_update as never);
          },
          proposal_action: (chunk: AgenticChunk) => {
            if (chunk.proposal_action)
              store.getState().addPending('proposalActions', chunk.proposal_action as never);
          },
        }
      : {}),
  };

  return {
    handlers,
    ignored: [...SCOPE_CHUNK_CONTRACTS[isMeetingScope ? 'meeting' : 'generic'].ignored],
    toolEvents,
    setToolEvents,
  };
}
